import React from 'react';
import { Link } from 'react-router-dom';

const title = 'Reports Overview'; // page title

const description = `Our comprehensive management system offers a powerful 
suite of 11 detailed reports, each designed to provide clear, actionable
 insights into a specific area of your business, from a high-level Business 
 Overview and Sales performance to granular tracking of Inventory, Expenses,
  and multi-currency transactions. These reports are the foundation for
   informed decision-making, helping you monitor financial health, optimize
    operations, and identify key trends, all easily accessible through a 
    powerful search function that allows you to quickly find any data 
    point for efficient analysis.`; // page description


const reports_images = [
    '/images/reports/report_overview.png', // 0
    '/images/reports/business-overview/profit-and-loss.png', // 1
    '/images/reports/business-overview/new.png', // 2
    '/images/reports/business-overview/searchbox-open.png', // 3
    '/images/reports/business-overview/newtab-and-exit.png', // 4
    '/images/reports/business-overview/export-item.png', // 5
    '/images/reports/business-overview/branch-filter.png', // 6
    '/images/reports/business-overview/month-filter.png', // 7
    '/images/reports/business-overview/compare-dropdown.png', // 8
    '/images/reports/business-overview/compare-report.png', // 9
    '/images/reports/report_overview.png',
    '/images/reports/report_search.png',
    '/images/reports/link_icon.svg',
];

const available_action = [
        {
            strong: 'Search',
            description: ' With our search option, you can easily find any report or financial data you need, all in one place.'
        },
        {
            strong: 'New Tab',
            description: `
            To get a closer look at a report, you can open it in a new tab. This lets you view it on its own, making it easier to see all the details clearly.`
        },
        {
            strong: 'Export',
            description: `
            With the Export button, you can download the data to share it with anyone you want.`
        },
        {
            strong: 'Exit',
            description: ' To leave this page, just click the red \'X\' button.'
        },
        {
            strong: 'Filter',
            description: ' You can filter your view by month or by branch to see exactly the data you need.'
        },
        {
            strong: 'Compare Periods',
            description: ' Our Compare Periods feature lets you see how your business is performing over different timeframes, so you can easily spot trends and make smart decisions.'
        },

]


const businessOverview_subtitle = [
    {
        title: 'Profit and Loss',
        description: `The Profit and Loss Report provides a clear view of your company's financial health by taking your combined Main Income, Other Income, and Non-Operating Income, then subtracting the Cost of Goods Sold (COGS) and all other Expenses to reveal your final total amount, which represents your net profit or loss for the period.`,
        id: 'profit-and-loss',
    },
    {
        title: 'Cash Flow Statement',
        description: `The Cash Flow Statement provides a clear view of how cash and cash equivalents move in and out of your business. It is broken down into three main sections: cash from operations, cash from investing, and cash from financing. This report is vital for understanding your liquidity and how you manage cash to support business activities.`,
        id: 'cash-flow-statement',
    },
    {
        title: 'Balance Sheet',
        description: `The Balance Sheet provides a snapshot of your company's financial position at a specific point in time. It is based on the fundamental accounting equation: Assets = Liabilities + Equity. This report helps you assess your company's solvency, liquidity, and overall financial strength.`,
        id: 'balance-sheet',
    },
    {
        title: 'Movement of Equity',
        description: `The Movement of Equity report details the changes in the value of ownership over a specific period. It accounts for transactions such as net income (or loss), owner contributions, and distributions or dividends. This report is essential for understanding how the owners' stake in the company has changed over time.`,
        id: 'movement-of-equity',
    },
];


const report_search = 'To search for specific data within any report, simply use the search bar located to the right of the report\'s title.';

const  reports = [
    {
        title:'Business Overview',
        route: '/reports/business-overview',
    },
    {
        title:'Accountant',
        route: '/reports/accountant',
    },
    {
        title:'Sales',
        route: '/reports/sales',
    },    {
        title:'Inventory',
        route: '/reports/inventory',
    },
    {
        title:'Payables',
        route: '/reports/payables',
    },
    {
        title:'Receivables',
        route: '/reports/receivables',
    },
    {
        title:'Purchase',
        route: '/reports/purchase',
    },
    {
        title:'Payments Received',
        route: '/reports/payments-received',
    },
    {
        title:'Payments Made',
        route: '/reports/payments-made',
    },
    {
        title:'Expense',
        route: '/reports/expense',
    },
    {
        title:'Currency',
        route: '/reports/currency',
    },
];

const Reports: React.FC = () => {

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">{title}</h1>
                    <p className="text-gray-600 mt-2">{description}</p>
                </div>

                {/* Main Banking Overview Image */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    <div className="mb-6">
                        <img
                            src={reports_images[10]}
                            alt="Products Main Page"
                            className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"
                        />
                    </div>
                </div>

                <p className="text-gray-600 my-2">{report_search}</p>
                {/* Main Banking Overview Image */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    <div className="mb-6">
                        <img
                            src={reports_images[11]}
                            alt="Products Main Page"
                            className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"
                        />
                    </div>
                </div>
            </div>
            <div className="mb-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-4 mb-6">
                    <h1 className="text-xl font-bold text-gray-900">Reports Quick Links</h1>
                    <p className="text-gray-600 mt-2">You can quickly access any report from the main menu by selecting its title from the comprehensive list.</p>
                    <ul className={'grid grid-cols-3 pt-4 gap-x-16 gap-y-2'}>
                        {
                            reports.map((report, index) => (
                                <li className={'hover:text-blue-700 text-sm p-2 border-b flex group'} key={index}>
                                    <Link className={'hover:underline'} to={report.route}>{report.title}</Link>
                                    <img className={'text-blue-700 hidden group-hover:block'} width={15} height={15} src={reports_images[12]} alt=""/>
                                </li>

                            ))
                        }
                    </ul>
                </div>
            </div>
            {/* Available action */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Available Actions</h2>
                <p className="text-gray-600 mb-4">
                    All Reports offers several action buttons in the top toolbar :
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
                    {
                        available_action.map((item,index)=>(
                            <li key={index}><strong>{item.strong}</strong></li>
                        ))
                    }
                </ul>

            </div>
            {/* Search */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">{available_action[0].strong}:</h2>
                <p className="text-gray-600 mb-4">
                    {available_action[0].description}
                </p>
                <img
                    src={reports_images[2]}
                    alt={`${businessOverview_subtitle[1].title} Report`}
                    className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>
                <p className="text-gray-600 mb-4 pt-4">
                    and
                </p>
                <img
                    src={reports_images[3]}
                    alt={`${businessOverview_subtitle[1].title} Report`}
                    className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>
            </div>
            {/* New Tab and Exit */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">{available_action[1].strong} and {available_action[3].strong}</h2>
                <p className="text-gray-600 mb-4">
                    {available_action[1].description}
                </p>
                <img
                    src={reports_images[4]}
                    alt={`${businessOverview_subtitle[1].title} Report`}
                    className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>
                <p className="text-gray-600 mb-4 pt-4">
                    and {available_action[4].description}
                </p>
                <img
                    src={reports_images[4]}
                    alt={`${businessOverview_subtitle[1].title} Report`}
                    className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>
            </div>
            {/* Export */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">{available_action[2].strong}:</h2>
                <p className="text-gray-600 mb-4">
                    {available_action[2].description}
                </p>
                <img
                    src={reports_images[5]}
                    alt={`${businessOverview_subtitle[1].title} Report`}
                    className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>
            </div>
            {/* Filter */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">{available_action[4].strong}:</h2>
                <p className="text-gray-600 mb-4">
                    {available_action[4].description}
                </p>
                <img
                    src={reports_images[6]}
                    alt={`${businessOverview_subtitle[1].title} Report`}
                    className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>
                <p className="text-gray-600 mb-4 pt-4">
                    and also with month filter
                </p>
                <img
                    src={reports_images[7]}
                    alt={`${businessOverview_subtitle[1].title} Report`}
                    className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>
            </div>
            {/* Compare Month */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">{available_action[5].strong}:</h2>
                <p className="text-gray-600 mb-4">
                    {available_action[5].description}
                </p>
                <img
                    src={reports_images[8]}
                    alt={`${businessOverview_subtitle[1].title} Report`}
                    className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>
                <p className="text-gray-600 mb-4 pt-4">
                    and custom compare period reports
                </p>
                <img
                    src={reports_images[9]}
                    alt={`${businessOverview_subtitle[1].title} Report`}
                    className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>
            </div>
        </div>
    );
};

export default Reports;
